/**
 * Company Profiles Service
 * Handles Firestore operations for multiple company profiles (BV's)
 */

import { doc, getDoc, setDoc, onSnapshot, serverTimestamp, Unsubscribe } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { 
  CompanyProfile, 
  migrateToCompanyProfile 
} from '@/types/branding.types';
import { getCompanyBranding } from './branding.service';
import { logger } from "@/lib/logger";
import { v4 as uuidv4 } from 'uuid';

const STORAGE_PATH = 'branding';

// Firestore document shape for structured address
interface FirestoreCompanyAddress {
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  lat?: number;
  lng?: number;
  formattedAddress?: string;
}

// Firestore document shape for bank account
interface FirestoreBankAccount {
  iban?: string;
  bic?: string;
  accountName?: string;
}

// Firestore document shape for company profile
interface FirestoreCompanyProfile {
  id?: string;
  name?: string;
  vatNumber?: string;
  kvkNumber?: string;
  address?: string; // Legacy string address
  addressData?: FirestoreCompanyAddress; // New structured address
  logoUrl?: string;
  bankAccount?: FirestoreBankAccount; // Bank account for invoices
  isDefault?: boolean;
  createdAt?: { toDate?: () => Date };
  updatedAt?: { toDate?: () => Date };
}

/**
 * Parse Firestore profile data to CompanyProfile
 */
function parseFirestoreProfile(p: FirestoreCompanyProfile): CompanyProfile {
  return {
    id: p.id || '',
    name: p.name || '',
    vatNumber: p.vatNumber || undefined,
    kvkNumber: p.kvkNumber || undefined,
    address: p.address || undefined,
    addressData: p.addressData ? {
      street: p.addressData.street || undefined,
      houseNumber: p.addressData.houseNumber || undefined,
      postalCode: p.addressData.postalCode || undefined,
      city: p.addressData.city || undefined,
      country: p.addressData.country || undefined,
      lat: p.addressData.lat,
      lng: p.addressData.lng,
      formattedAddress: p.addressData.formattedAddress || undefined,
    } : undefined,
    logoUrl: p.logoUrl || undefined,
    bankAccount: p.bankAccount ? {
      iban: p.bankAccount.iban || undefined,
      bic: p.bankAccount.bic || undefined,
      accountName: p.bankAccount.accountName || undefined,
    } : undefined,
    isDefault: p.isDefault || false,
    createdAt: p.createdAt?.toDate?.() || undefined,
    updatedAt: p.updatedAt?.toDate?.() || undefined,
  };
}

/**
 * Get company profiles (one-time fetch)
 * Includes migration from legacy single-company branding
 */
export async function getCompanyProfiles(teamId: string): Promise<CompanyProfile[]> {
  try {
    // Try new companyProfiles location first
    const profilesRef = doc(db, 'teams', teamId, 'settings', 'companyProfiles');
    const profilesSnap = await getDoc(profilesRef);
    
    if (profilesSnap.exists()) {
      const data = profilesSnap.data() as { profiles?: FirestoreCompanyProfile[] };
      const rawProfiles = data.profiles || [];
      const profiles: CompanyProfile[] = rawProfiles.map(parseFirestoreProfile);
      
      // Ensure at least one default
      if (profiles.length > 0 && !profiles.some(p => p.isDefault) && profiles[0]) {
        profiles[0].isDefault = true;
      }
      
      return profiles;
    }
    
    // Fallback: migrate from legacy companyBranding
    const legacyBranding = await getCompanyBranding(teamId);
    if (legacyBranding && (legacyBranding.companyName || legacyBranding.logoUrl)) {
      const migratedProfile = migrateToCompanyProfile(legacyBranding, uuidv4());
      
      // Save migrated profile to new location
      await saveCompanyProfiles(teamId, [migratedProfile]);
      
      logger.info('[CompanyProfilesService] Migrated legacy branding to company profile');
      return [migratedProfile];
    }
    
    // No profiles exist - return empty array
    return [];
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied') {
      logger.warn('[CompanyProfilesService] Permission denied - returning empty array');
      return [];
    }
    
    logger.error('[CompanyProfilesService] Failed to get profiles:', error);
    return [];
  }
}

/**
 * Watch company profiles (real-time listener)
 */
export function watchCompanyProfiles(
  teamId: string,
  callback: (profiles: CompanyProfile[]) => void
): Unsubscribe {
  const docRef = doc(db, 'teams', teamId, 'settings', 'companyProfiles');
  
  return onSnapshot(
    docRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        // Try migration from legacy (async, but we handle it)
        void getCompanyBranding(teamId).then((legacyBranding) => {
          if (legacyBranding && (legacyBranding.companyName || legacyBranding.logoUrl)) {
            const migratedProfile = migrateToCompanyProfile(legacyBranding, uuidv4());
            callback([migratedProfile]);
            return;
          }
          callback([]);
        });
        return;
      }
      
      const data = snapshot.data() as { profiles?: FirestoreCompanyProfile[] };
      const rawProfiles = data.profiles || [];
      const profiles: CompanyProfile[] = rawProfiles.map(parseFirestoreProfile);
      
      // Ensure at least one default
      if (profiles.length > 0 && !profiles.some(p => p.isDefault) && profiles[0]) {
        profiles[0].isDefault = true;
      }
      
      callback(profiles);
    },
    (error: unknown) => {
      if (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied') {
        logger.warn('[CompanyProfilesService] Permission denied - returning empty array');
        callback([]);
        return;
      }
      
      logger.error('[CompanyProfilesService] Snapshot error:', error);
      callback([]);
    }
  );
}

/**
 * Save all company profiles
 */
export async function saveCompanyProfiles(
  teamId: string,
  profiles: CompanyProfile[]
): Promise<void> {
  try {
    const docRef = doc(db, 'teams', teamId, 'settings', 'companyProfiles');
    
    // Ensure exactly one default
    const hasDefault = profiles.some(p => p.isDefault);
    if (!hasDefault && profiles.length > 0 && profiles[0]) {
      profiles[0].isDefault = true;
    }
    
    // Serialize profiles for Firestore
    // Note: serverTimestamp() is not supported inside arrays, so we use Date objects
    // Also, Firestore doesn't accept undefined values, so we convert to null
    const now = new Date();
    const serializedProfiles = profiles.map(p => {
      // Serialize addressData, converting undefined to null
      const serializedAddressData = p.addressData ? {
        street: p.addressData.street ?? null,
        houseNumber: p.addressData.houseNumber ?? null,
        postalCode: p.addressData.postalCode ?? null,
        city: p.addressData.city ?? null,
        country: p.addressData.country ?? null,
        lat: p.addressData.lat ?? null,
        lng: p.addressData.lng ?? null,
        formattedAddress: p.addressData.formattedAddress ?? null,
      } : null;

      // Serialize bankAccount, converting undefined to null
      const serializedBankAccount = p.bankAccount ? {
        iban: p.bankAccount.iban ?? null,
        bic: p.bankAccount.bic ?? null,
        accountName: p.bankAccount.accountName ?? null,
      } : null;

      return {
        id: p.id,
        name: p.name,
        vatNumber: p.vatNumber ?? null,
        kvkNumber: p.kvkNumber ?? null,
        address: p.address ?? null,
        addressData: serializedAddressData,
        logoUrl: p.logoUrl ?? null,
        bankAccount: serializedBankAccount,
        isDefault: p.isDefault ?? false,
        createdAt: p.createdAt ?? now,
        updatedAt: now,
      };
    });
    
    await setDoc(docRef, {
      profiles: serializedProfiles,
      updatedAt: serverTimestamp(), // serverTimestamp() works at document level
    });
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied') {
      throw new Error("You don't have access to update team settings.");
    }
    
    logger.error('[CompanyProfilesService] Failed to save profiles:', error);
    throw error;
  }
}

/**
 * Add a new company profile
 */
export async function addCompanyProfile(
  teamId: string,
  profile: Omit<CompanyProfile, 'id' | 'createdAt' | 'updatedAt'>
): Promise<CompanyProfile> {
  const profiles = await getCompanyProfiles(teamId);
  
  const newProfile: CompanyProfile = {
    ...profile,
    id: uuidv4(),
    createdAt: new Date(),
    updatedAt: new Date(),
    // If this is the first profile, make it default
    isDefault: profiles.length === 0 ? true : profile.isDefault,
  };
  
  // If new profile is default, unset others
  if (newProfile.isDefault) {
    profiles.forEach(p => { p.isDefault = false; });
  }
  
  await saveCompanyProfiles(teamId, [...profiles, newProfile]);
  return newProfile;
}

/**
 * Ensure a company profile exists, creating a default one if needed
 * Returns the profile ID (existing or newly created)
 */
async function ensureCompanyProfile(
  teamId: string,
  profileId: string | null | undefined
): Promise<{ profiles: CompanyProfile[]; targetId: string }> {
  const profiles = await getCompanyProfiles(teamId);
  
  // If profiles exist and we have a valid ID, return as-is
  if (profiles.length > 0 && profileId) {
    const exists = profiles.some(p => p.id === profileId);
    if (exists) {
      return { profiles, targetId: profileId };
    }
  }
  
  // If no profiles exist at all, create a default one
  if (profiles.length === 0) {
    const defaultId = profileId || uuidv4();
    const defaultProfile: CompanyProfile = {
      id: defaultId,
      name: '',
      isDefault: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return { profiles: [defaultProfile], targetId: defaultId };
  }
  
  // Profiles exist but ID not found - use first profile
  return { profiles, targetId: profiles[0]?.id || uuidv4() };
}

/**
 * Update an existing company profile
 * Safe for first-time writes - creates profile if it doesn't exist
 */
export async function updateCompanyProfile(
  teamId: string,
  profileId: string,
  updates: Partial<Omit<CompanyProfile, 'id' | 'createdAt'>>
): Promise<void> {
  // Ensure profile exists (creates default if needed)
  const { profiles, targetId } = await ensureCompanyProfile(teamId, profileId);
  const index = profiles.findIndex(p => p.id === targetId);
  
  // If setting as default, unset others
  if (updates.isDefault) {
    profiles.forEach(p => { p.isDefault = false; });
  }
  
  const existingProfile = profiles[index];
  if (existingProfile) {
    profiles[index] = {
      ...existingProfile,
      ...updates,
      updatedAt: new Date(),
    };
  }
  
  await saveCompanyProfiles(teamId, profiles);
}

/**
 * Delete a company profile
 * Cannot delete the last remaining profile
 */
export async function deleteCompanyProfile(
  teamId: string,
  profileId: string
): Promise<void> {
  const profiles = await getCompanyProfiles(teamId);
  
  if (profiles.length <= 1) {
    throw new Error('Cannot delete the last company profile');
  }
  
  const profileToDelete = profiles.find(p => p.id === profileId);
  const remainingProfiles = profiles.filter(p => p.id !== profileId);
  
  // If deleted profile was default, set first remaining as default
  if (profileToDelete?.isDefault && remainingProfiles.length > 0 && remainingProfiles[0]) {
    remainingProfiles[0].isDefault = true;
  }
  
  await saveCompanyProfiles(teamId, remainingProfiles);
}

/**
 * Set a profile as the default
 */
export async function setDefaultProfile(
  teamId: string,
  profileId: string
): Promise<void> {
  const profiles = await getCompanyProfiles(teamId);
  
  profiles.forEach(p => {
    p.isDefault = p.id === profileId;
  });
  
  await saveCompanyProfiles(teamId, profiles);
}

/**
 * Get the default company profile
 */
export async function getDefaultProfile(teamId: string): Promise<CompanyProfile | null> {
  const profiles = await getCompanyProfiles(teamId);
  return profiles.find(p => p.isDefault) || profiles[0] || null;
}

/**
 * Get a specific company profile by ID
 * Falls back to default if not found
 */
export async function getCompanyProfileById(
  teamId: string,
  profileId: string | undefined | null
): Promise<CompanyProfile | null> {
  const profiles = await getCompanyProfiles(teamId);
  
  if (profileId) {
    const profile = profiles.find(p => p.id === profileId);
    if (profile) return profile;
  }
  
  // Fallback to default
  return profiles.find(p => p.isDefault) || profiles[0] || null;
}

/**
 * Upload company logo to Firebase Storage
 */
export async function uploadCompanyLogo(
  teamId: string,
  profileId: string,
  file: File
): Promise<string> {
  try {
    const ext = file.name.split('.').pop() || 'png';
    const storagePath = `${STORAGE_PATH}/${teamId}/profiles/${profileId}/logo.${ext}`;
    const storageRef = ref(storage, storagePath);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    logger.error('[CompanyProfilesService] Failed to upload logo:', error);
    throw error;
  }
}

/**
 * Delete company logo from Firebase Storage
 */
export async function deleteCompanyLogo(
  _teamId: string,
  _profileId: string,
  logoUrl: string
): Promise<void> {
  try {
    // Extract path from URL
    const storageRef = ref(storage, logoUrl);
    await deleteObject(storageRef);
  } catch (error) {
    // Ignore if file doesn't exist
    logger.warn('[CompanyProfilesService] Failed to delete logo:', error);
  }
}
